import { SvgIconDeleteOff } from "@icons";
import {
    AppState,
    AssetAttribute,
    assetAttributeNameSelector,
    assetFormByKey,
    emptyExistingAdUnitAction
} from "@store";
import * as cn from "classnames";
import { FormBindings, InvalidFormInfo, ValidFormInfo } from "rd-redux-forms";
import { FetchingState } from "rd-redux-http";
import * as React from "react";
import { connect, Dispatch } from "react-redux";
import { AssetAttributeFormMeta } from "store/adunit-builder/adUnitAssetBuilderForm";
import { Fieldset } from "../Fieldset";
import { SvgIcon } from "../SvgIcon";
import "./AssetEditForm.css";
import { ColorPickerComponent, FileUploader, MultipleFileUploader } from "./editors";
import { ExistingAdUnitSelector } from "./ExistingAdUnitSelector";

interface AssetEditFormComponentProps {
    dispatch: Dispatch;

    meta: AssetAttributeFormMeta;
    form: ValidFormInfo<any> | InvalidFormInfo<any>;
    createEvents: (dispatch: Dispatch) => FormBindings<any>;

    onRemove?: () => void;

    assets: AssetAttribute[];
    className?: string;
    assetIcon?: string;
    min?: number;
    currentLength?: number;
    canConfigureNewAdunit: boolean;

    onAutoCleanExistingAdUnit: () => void;
}

interface AssetEditFormComponentState {
    flag: boolean;
}

class AssetEditFormComponent extends React.Component<AssetEditFormComponentProps, AssetEditFormComponentState> {
    constructor(props: AssetEditFormComponentProps) {
        super(props);
        this.state = {
            flag: false
        };
    }

    onHover() {
        this.setState({
            ...this.state,
            flag: true
        });
    }

    onUnHover() {
        this.setState({
            ...this.state,
            flag: false
        });
    }

    render() {
        const {
            assets,
            form,
            meta,
            createEvents,
            dispatch,
            className,
            onRemove,
            assetIcon,
            min,
            currentLength,
            canConfigureNewAdunit,
            onAutoCleanExistingAdUnit
        } = this.props;
        if (!form) {
            return null;
        }

        const $events = createEvents(dispatch);

        return (
            <>
                {assets &&
                    assets.map(a => {
                        const field = form.fields[a.name];
                        const $e = $events.fields[a.name];

                        if (a.name === "name") {
                            return (
                                <div key={"Id-" + a.title}>
                                    <div className="AssetEditForm__main-block tour-campaign-ad-unit-builder-main">
                                        <ExistingAdUnitSelector />
                                        <Fieldset
                                            className={cn("AssetEditForm__custom-fieldset")}
                                            label="New Adunit"
                                            key={a.name}
                                            errorMessage={field}
                                            showLastError={a.type === "image"}
                                            isMandatory={true}
                                        >
                                            <input
                                                value={field.value || ""}
                                                {...$e}
                                                onChange={value => {
                                                    onAutoCleanExistingAdUnit();
                                                    $e.onChange(value);
                                                }}
                                            />
                                        </Fieldset>
                                    </div>
                                    <div
                                        className={cn(
                                            "AssetEditForm__main-block-footer",
                                            canConfigureNewAdunit ? "" : "AssetEditForm__main-block-footer_disabled"
                                        )}
                                    >
                                        New Adunit configuration
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })}
                <div className={cn("AssetEditForm", className, canConfigureNewAdunit ? "" : "AssetEditForm_disabled")}>
                    {assetIcon && onRemove && <img className="AssetEditForm__asset-icon" src={assetIcon} />}
                    {assets.map(a => {
                        const field = form.fields[a.name];
                        const $e = $events.fields[a.name];

                        if (a.name === "name") return null;

                        return (
                            <Fieldset
                                className={cn(
                                    "AssetEditForm__custom-fieldset",
                                    a.type === "image" ? "custom-image-upload-fieldset" : "",
                                    a.type === "color" ? "custom-color-picker-fieldset" : ""
                                )}
                                label={assetAttributeNameSelector(a)}
                                key={a.name}
                                errorMessage={field}
                                showLastError={a.type === "image"}
                                isMandatory={true}
                            >
                                {(function renderInput() {
                                    switch (a.type) {
                                        case "color":
                                            return <ColorPickerComponent value={field.value || ""} {...$e} />;
                                        case "image":
                                            return "min" in a ? (
                                                <MultipleFileUploader
                                                    asset={a as any}
                                                    field={field}
                                                    fieldEvents={$e}
                                                    fieldName={a.name}
                                                    meta={meta}
                                                />
                                            ) : (
                                                <FileUploader
                                                    asset={a as any}
                                                    field={field}
                                                    fieldEvents={$e}
                                                    fieldName={a.name}
                                                    meta={meta}
                                                />
                                            );
                                        default:
                                            return field && <input value={field.value || ""} {...$e} />;
                                    }
                                })()}
                            </Fieldset>
                        );
                    })}
                    {onRemove &&
                        currentLength > min && (
                            <button className={cn("AssetEditForm__remove-button")} onClick={onRemove}>
                                <SvgIcon className="AssetEditForm__remove-svg" svg={SvgIconDeleteOff} />
                            </button>
                        )}
                </div>
            </>
        );
    }
}

export interface AssetEditFormProps {
    assetPropertyName?: string;
    index?: number;
    className?: string;
    min?: number;
    currentLength?: number;
    canConfigureNewAdunit: boolean;
    onRemove?: (assetProperyName?: string, index?: number) => void;
}

export const AssetEditForm = connect(
    (appState: AppState, ownProps: AssetEditFormProps): Partial<AssetEditFormComponentProps> => {
        const result = assetFormByKey(appState, ownProps.assetPropertyName, ownProps.index || 0);
        if (!result) {
            return {};
        }

        const selectedAdFormat = appState.campaignState.campaignMediaPlanForm.fields.adUnitFormat;
        const selectedAdType = appState.campaignState.campaignCreativesForm.fields.adUnitType;

        const adUnitMapping =
            selectedAdFormat &&
            FetchingState.isSuccess(appState.mappings.adunits) &&
            appState.mappings.adunits.data.find(a => a.size === selectedAdFormat);

        const type = adUnitMapping && selectedAdType && adUnitMapping.types.find(a => a.name === selectedAdType);

        const assetIcon = type && type.reportingIcon;

        return {
            assets: result.assets,
            form: result.data,
            createEvents: result.createBindings,
            meta: result.meta,
            className: ownProps.className,
            assetIcon,
            onRemove: ownProps.onRemove
                ? () => ownProps.onRemove(ownProps.assetPropertyName, ownProps.index)
                : undefined,
            min: ownProps.min,
            currentLength: ownProps.currentLength,
            canConfigureNewAdunit: ownProps.canConfigureNewAdunit
        };
    },
    (dispatch: Dispatch): Partial<AssetEditFormComponentProps> => {
        return {
            dispatch,
            onAutoCleanExistingAdUnit: () => {
                dispatch(emptyExistingAdUnitAction());
            }
        };
    }
)(AssetEditFormComponent);
